import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';

import {
  AlertModal,
  Badge,
  Button,
  EmptyState,
  PageHeader,
  PageLayout,
  PageToolbar,
  SearchInput,
  Skeleton,
  Table,
  toast,
} from '@keboola/design';
import { BadgeCheck, Pencil, Plus } from '@keboola/design/icons';

import { QueryError } from '../../components/QueryError';
import { ApproveVendorModal } from '../../features/admin/components/ApproveVendorModal';
import { VendorFormModal } from '../../features/admin/components/VendorFormModal';
import { useAdminVendors, useDeleteVendor } from '../../features/admin/queries';
import { getApiErrorMessage } from '../../features/auth/errors';
import { projectUrlSchema } from '../../features/vendors/schemas';

export const Route = createFileRoute('/admin/vendors')({
  component: AdminVendorsPage,
  head: () => ({ meta: [{ title: 'Vendors | Keboola Developer Portal admin' }] }),
});

/** A pending, self-registered vendor — apps-api gives these a temporary id until an admin approves. */
const isPending = (id: string) => id.startsWith('_');

function AdminVendorsPage() {
  const { data: vendors, isLoading, isError, error, refetch } = useAdminVendors();
  const deleteVendor = useDeleteVendor();
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (
      (vendors ?? [])
        .filter(
          (vendor) =>
            needle === '' ||
            [vendor.id, vendor.name, vendor.email ?? ''].some((value) =>
              value.toLowerCase().includes(needle),
            ),
        )
        // Pending vendors first: they need action.
        .sort((a, b) => {
          if (isPending(a.id) !== isPending(b.id)) return isPending(a.id) ? -1 : 1;
          return a.name.localeCompare(b.name);
        })
    );
  }, [vendors, query]);

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Index
          title="Vendors"
          description="Every vendor, approved or waiting."
          action={
            <VendorFormModal
              trigger={
                <Button variant="primary">
                  <Plus />
                  Add vendor
                </Button>
              }
            />
          }
        />
      </PageLayout.Header>

      {isLoading && <Skeleton className="tw:h-64" />}

      <QueryError
        isError={isError}
        hasData={vendors !== undefined}
        error={error}
        onRetry={() => void refetch()}
        loadTitle="Could not load the vendors"
        refreshTitle="Could not refresh the vendors"
        staleNote="Showing the vendors from the last successful load."
        titleAs="h2"
      />

      {vendors !== undefined && (
        <>
          <PageToolbar>
            <PageToolbar.Search>
              <SearchInput
                allowClear
                placeholder="Search by ID, name or email"
                value={query}
                onValueChange={setQuery}
              />
            </PageToolbar.Search>
          </PageToolbar>

          {visible.length === 0 ? (
            <EmptyState variant="compact">
              <EmptyState.Description>No vendor matches “{query}”.</EmptyState.Description>
            </EmptyState>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Name, email and address</Table.Head>
                  <Table.Head>ID</Table.Head>
                  <Table.Head>Status</Table.Head>
                  <Table.Head>Test project</Table.Head>
                  <Table.Head>
                    <span className="tw:sr-only">Actions</span>
                  </Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {visible.map((vendor) => (
                  <Table.Row key={vendor.id}>
                    <Table.Cell>
                      <div className="tw:flex tw:flex-col tw:gap-0.5">
                        <span className="tw:font-medium">{vendor.name}</span>
                        {vendor.email !== undefined && vendor.email !== '' && (
                          <a
                            href={`mailto:${vendor.email}`}
                            className="tw:text-xs tw:text-primary tw:underline"
                          >
                            {vendor.email}
                          </a>
                        )}
                        {vendor.address !== undefined && vendor.address !== '' && (
                          <span className="tw:text-xs tw:whitespace-pre-line tw:text-muted-foreground">
                            {vendor.address}
                          </span>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {isPending(vendor.id) ? (
                        // No link: a pending vendor owns no components yet.
                        <code className="tw:font-mono tw:text-xs tw:break-all">{vendor.id}</code>
                      ) : (
                        <Link
                          to="/admin"
                          search={{ query: vendor.id }}
                          className="tw:font-mono tw:text-xs"
                        >
                          {vendor.id}
                        </Link>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="tw:flex tw:flex-col tw:items-start tw:gap-1">
                        {vendor.isApproved === true ? (
                          <Badge color="green-inverse">Approved</Badge>
                        ) : (
                          <Badge color="amber">Waiting</Badge>
                        )}
                        <Badge color="gray-inverse">
                          {vendor.isPublic === false ? 'Private' : 'Public'}
                        </Badge>
                        {vendor.isTrusted === true && (
                          <span className="tw:inline-flex tw:items-center tw:gap-1 tw:text-xs tw:text-primary">
                            <BadgeCheck className="tw:size-3" />
                            Trusted
                          </span>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <ProjectLink url={vendor.projectUrl} />
                    </Table.Cell>
                    <Table.Cell>
                      <div className="tw:flex tw:justify-end tw:gap-2">
                        <VendorFormModal
                          vendor={vendor}
                          trigger={
                            <Button
                              variant="invisible"
                              size="icon-sm"
                              aria-label={`Edit ${vendor.name}`}
                            >
                              <Pencil />
                            </Button>
                          }
                        />
                        {vendor.isApproved !== true && <ApproveVendorModal vendor={vendor} />}
                        <AlertModal
                          // Caught: `AlertModal` closes on settle, so a rejection would vanish silently.
                          onConfirm={async () => {
                            try {
                              await deleteVendor.mutateAsync(vendor.id);
                              toast.success(`${vendor.name} was removed.`);
                            } catch (error) {
                              toast.error(
                                getApiErrorMessage(error, `Could not remove ${vendor.name}.`),
                              );
                            }
                          }}
                        >
                          <AlertModal.Trigger asChild>
                            <Button variant="danger" size="small">
                              Remove
                            </Button>
                          </AlertModal.Trigger>
                          <AlertModal.Content
                            variant="danger"
                            title={`Remove ${vendor.name}?`}
                            confirmLabel="Remove vendor"
                          >
                            Its members lose access to everything it owns. Components already
                            published under <code>{vendor.id}</code> keep running, but nobody will
                            be able to maintain them.
                          </AlertModal.Content>
                        </AlertModal>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </>
      )}
    </PageLayout>
  );
}

/**
 * The vendor's test-project link.
 *
 * - Re-checks the protocol: stored `projectUrl` may predate validation, and `javascript:` would run.
 * - An unrecognised value renders no link, as on the vendor profile.
 */
function ProjectLink({ url }: { url: string | null | undefined }) {
  const parsed = projectUrlSchema.safeParse(url);
  if (!parsed.success) return null;

  return (
    <a
      href={parsed.data}
      target="_blank"
      rel="noopener noreferrer"
      className="tw:text-xs tw:text-primary tw:underline"
    >
      Open project
    </a>
  );
}
